import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam, useInviteMember, useCancelInvitation, useRemoveMember } from '../../hooks/useTeam';
import { useAttorney } from '../../context/AttorneyContext';
import './MyTeam.css';

/**
 * MyTeam Component
 * Allows org admins to manage their team members and invitations
 */
function MyTeam() {
  const { t } = useTranslation('attorney');
  const { isAdmin, firmName } = useAttorney();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('org:attorney');
  const [confirmRemove, setConfirmRemove] = useState(null);

  const { data, isLoading, error, refetch } = useTeam();
  const inviteMutation = useInviteMember();
  const cancelInvitationMutation = useCancelInvitation();
  const removeMemberMutation = useRemoveMember();

  const members = data?.members || [];
  const invitations = data?.invitations || [];

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    try {
      await inviteMutation.mutateAsync({ email: inviteEmail.trim(), role: inviteRole });
      setInviteEmail('');
      setInviteRole('org:attorney');
      setShowInviteModal(false);
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await cancelInvitationMutation.mutateAsync(invitationId);
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await removeMemberMutation.mutateAsync(userId);
      setConfirmRemove(null);
    } catch (err) {
      // Error handled by mutation
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'org:admin':
        return t('attorney.team.roleAdmin', 'Admin');
      case 'org:attorney':
        return t('attorney.team.roleAttorney', 'Attorney');
      case 'org:member':
        return t('attorney.team.roleMember', 'Member');
      default:
        return role;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isAdmin) {
    return (
      <div className="myteam-container">
        <div className="myteam-no-access">
          <p>{t('attorney.team.noAccess', 'Only organization admins can manage team members.')}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="myteam-container">
        <div className="myteam-loading">
          <div className="loading-spinner" />
          <p>{t('attorney.team.loading', 'Loading team...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="myteam-container">
        <div className="myteam-error">
          <p>{t('attorney.team.error', 'Failed to load team')}</p>
          <button onClick={() => refetch()}>
            {t('attorney.team.retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="myteam-container">
      <div className="myteam-header">
        <div>
          <h1>{t('attorney.team.title', 'My Team')}</h1>
          <p className="myteam-subtitle">{firmName}</p>
        </div>
        <button
          className="btn-invite"
          onClick={() => setShowInviteModal(true)}
        >
          {t('attorney.team.inviteMember', 'Invite Member')}
        </button>
      </div>

      {/* Team Members */}
      <div className="myteam-section">
        <h2>{t('attorney.team.members', 'Team Members')} ({members.length})</h2>
        {members.length === 0 ? (
          <div className="myteam-empty">
            <p>{t('attorney.team.noMembers', 'No team members yet')}</p>
          </div>
        ) : (
          <div className="myteam-list">
            {members.map((member) => (
              <div key={member.userId} className="team-member-card">
                <div className="member-avatar">
                  {(member.name || member.email || '?')[0].toUpperCase()}
                </div>
                <div className="member-info">
                  <span className="member-name">
                    {member.name || member.email}
                    {member.isCurrentUser && (
                      <span className="member-you">{t('attorney.team.you', '(you)')}</span>
                    )}
                  </span>
                  <span className="member-email">{member.email}</span>
                </div>
                <span className={`member-role role-${member.role?.replace(':', '-')}`}>
                  {getRoleLabel(member.role)}
                </span>
                {!member.isCurrentUser && (
                  <button
                    className="btn-remove"
                    onClick={() => setConfirmRemove(member)}
                    disabled={removeMemberMutation.isPending}
                  >
                    {t('attorney.team.remove', 'Remove')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      <div className="myteam-section">
        <h2>{t('attorney.team.pendingInvitations', 'Pending Invitations')} ({invitations.length})</h2>
        {invitations.length === 0 ? (
          <div className="myteam-empty">
            <p>{t('attorney.team.noInvitations', 'No pending invitations')}</p>
          </div>
        ) : (
          <div className="myteam-list">
            {invitations.map((invitation) => (
              <div key={invitation.id} className="invitation-card">
                <div className="invitation-icon">✉️</div>
                <div className="invitation-info">
                  <span className="invitation-email">{invitation.email}</span>
                  <span className="invitation-meta">
                    {t('attorney.team.sentOn', 'Sent')}: {formatDate(invitation.createdAt)}
                  </span>
                </div>
                <span className={`member-role role-${invitation.role?.replace(':', '-')}`}>
                  {getRoleLabel(invitation.role)}
                </span>
                <button
                  className="btn-cancel-invite"
                  onClick={() => handleCancelInvitation(invitation.id)}
                  disabled={cancelInvitationMutation.isPending}
                >
                  {t('attorney.team.cancelInvite', 'Cancel')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
            <div className="invite-modal-header">
              <h2>{t('attorney.team.inviteTitle', 'Invite Team Member')}</h2>
              <button className="modal-close-btn" onClick={() => setShowInviteModal(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleInvite}>
              <div className="form-group">
                <label>{t('attorney.team.emailLabel', 'Email Address')}</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={t('attorney.team.emailPlaceholder', 'attorney@example.com')}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>{t('attorney.team.roleLabel', 'Role')}</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  <option value="org:attorney">
                    {t('attorney.team.roleAttorney', 'Attorney')} - {t('attorney.team.roleAttorneyDesc', 'Can view and work on assigned cases')}
                  </option>
                  <option value="org:admin">
                    {t('attorney.team.roleAdmin', 'Admin')} - {t('attorney.team.roleAdminDesc', 'Can manage team and assign work')}
                  </option>
                </select>
              </div>

              <div className="invite-info">
                <p>
                  {t('attorney.team.inviteInfo', 'An email with a magic link will be sent to invite them to join your organization.')}
                </p>
              </div>

              {inviteMutation.error && (
                <div className="invite-error">
                  {inviteMutation.error.response?.data?.message || inviteMutation.error.message}
                </div>
              )}

              <div className="invite-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviteMutation.isPending}
                >
                  {t('attorney.team.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-send-invite"
                  disabled={!inviteEmail.trim() || inviteMutation.isPending}
                >
                  {inviteMutation.isPending
                    ? t('attorney.team.sending', 'Sending...')
                    : t('attorney.team.sendInvite', 'Send Invitation')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {confirmRemove && (
        <div className="modal-overlay" onClick={() => setConfirmRemove(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('attorney.team.confirmRemoveTitle', 'Remove Team Member')}</h2>
            <p>
              {t('attorney.team.confirmRemoveMessage', 'Are you sure you want to remove {{name}} from the organization?', {
                name: confirmRemove.name || confirmRemove.email
              })}
            </p>
            <div className="confirm-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setConfirmRemove(null)}
                disabled={removeMemberMutation.isPending}
              >
                {t('attorney.team.cancel', 'Cancel')}
              </button>
              <button
                className="btn-confirm-remove"
                onClick={() => handleRemoveMember(confirmRemove.userId)}
                disabled={removeMemberMutation.isPending}
              >
                {removeMemberMutation.isPending
                  ? t('attorney.team.removing', 'Removing...')
                  : t('attorney.team.confirmRemove', 'Remove')
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyTeam;
